// The dashboard's date filter (D-144): one day or a range of days in the company's time zone, Today unless the address
// says otherwise, so a refresh or a shared link shows the same days. Pure functions, tested.

export type Preset = "today" | "yesterday" | "7d" | "30d";

export interface DayRange {
  /** "YYYY-MM-DD", both days included. */
  from: string;
  to: string;
  preset: Preset | "custom";
}

/** The most days the server gives at a time. */
export const MAX_DAYS = 93;

export const PRESETS: { key: Preset; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A day moved by n days. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** How many days a range covers, both ends counted. */
export function dayCount(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** Every day of a range, oldest first. */
export function rangeDays(r: { from: string; to: string }): string[] {
  const days: string[] = [];
  for (let d = r.from; d <= r.to && days.length < MAX_DAYS; d = addDays(d, 1)) days.push(d);
  return days;
}

const isDay = (s: string) => DAY.test(s) && addDays(s, 0) === s;

export function presetRange(preset: Preset, today: string): DayRange {
  switch (preset) {
    case "yesterday":
      return { from: addDays(today, -1), to: addDays(today, -1), preset };
    case "7d":
      return { from: addDays(today, -6), to: today, preset };
    case "30d":
      return { from: addDays(today, -29), to: today, preset };
    default:
      return { from: today, to: today, preset: "today" };
  }
}

/** Two days a person chose, or null when they make no range the dashboard can show: a day missing, the first after
 * the last, a day after today, or more than 93 days. */
export function customRange(from: string, to: string, today: string): DayRange | null {
  if (!isDay(from) || !isDay(to) || from > to || to > today || dayCount(from, to) > MAX_DAYS) return null;
  return { from, to, preset: "custom" };
}

/** The range in the address (?range=yesterday, or ?from=…&to=…); Today when there is none or it cannot be shown. */
export function rangeFromParams(params: URLSearchParams, today: string): DayRange {
  const preset = params.get("range");
  if (PRESETS.some((p) => p.key === preset)) return presetRange(preset as Preset, today);
  return customRange(params.get("from") ?? "", params.get("to") ?? "", today) ?? presetRange("today", today);
}

/** The address for a range: a preset by name, other days as they are. */
export function rangeParams(r: DayRange): Record<string, string> {
  return r.preset === "custom" ? { from: r.from, to: r.to } : { range: r.preset };
}

const SHORT = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", timeZone: "UTC" });
const LONG = new Intl.DateTimeFormat("en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const at = (day: string) => new Date(`${day}T00:00:00Z`);

/** The days themselves: "Wed, 7 Oct", or "1 Oct – 7 Oct". */
export function rangeDates(r: { from: string; to: string }): string {
  return r.from === r.to ? LONG.format(at(r.from)) : `${SHORT.format(at(r.from))} – ${SHORT.format(at(r.to))}`;
}

/** The range by name where it has one ("Today", "Last 7 days"), otherwise its days. */
export function rangeLabel(r: DayRange): string {
  return PRESETS.find((p) => p.key === r.preset)?.label ?? rangeDates(r);
}

/** A day as a chart's short label: "7 Oct". */
export function shortDay(day: string): string {
  return SHORT.format(at(day));
}
